import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { VideoCard } from '../components/VideoCard';
import { useAppStore } from '../store/appStore';
import { filterVideos, getWatchedVideoIds, orderVideosByFreshnessThenWatchStatus, videoMatchesSearchQuery, type Video } from '@kidstube/shared';
import './SearchPage.css';

interface ApiErrorBody {
    error?: string;
    code?: string;
    detail?: string;
    errorId?: string;
    retryable?: boolean;
}

interface SearchFailureDetails {
    message: string;
    detail: string;
}

const SEARCH_ERROR_LABELS: Record<string, string> = {
    youtube_api_key_missing: 'YouTube API key is missing on the server.',
    youtube_api_key_rejected: 'YouTube rejected the API key. Check key restrictions.',
    youtube_api_not_enabled: 'YouTube Data API v3 is not enabled for this key.',
    youtube_quota_or_rate_limit: 'YouTube quota or rate limit was reached.',
    youtube_api_forbidden: 'YouTube blocked this API request. Check key permissions and restrictions.',
    youtube_api_bad_request: 'The search request sent to YouTube was invalid.',
    youtube_api_error: 'YouTube returned an error to the search service.',
    search_internal_error: 'The search service hit an internal error.'
};

async function readSearchFailure(response: Response): Promise<SearchFailureDetails> {
    let body: ApiErrorBody | null = null;

    try {
        body = await response.json() as ApiErrorBody;
    } catch {
        body = null;
    }

    const code = body?.code;
    const label = code ? SEARCH_ERROR_LABELS[code] : undefined;
    const errorId = body?.errorId ? ` Error ID: ${body.errorId}.` : '';
    const detail = body?.detail || label || body?.error || `Search service returned HTTP ${response.status}.`;
    const reason = label && body?.detail ? `${label} ${body.detail}` : label || detail;

    return {
        message: 'Search is having trouble. Showing saved videos that match.',
        detail: `${reason}${errorId}`
    };
}

function readNetworkSearchFailure(error: unknown): SearchFailureDetails {
    const message = error instanceof Error ? error.message : 'Unknown browser network error';
    return {
        message: 'Search is having trouble. Showing saved videos that match.',
        detail: `The browser could not reach the search service. ${message}`
    };
}

function searchCacheKey(query: string): string {
    return query.trim().toLowerCase();
}

export function SearchPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialQuery = searchParams.get('q') || '';
    const initialCache = useAppStore.getState().searchCache;
    const hasInitialCache = Boolean(
        initialQuery && initialCache?.queryKey === searchCacheKey(initialQuery)
    );
    const initialCachedVideos = hasInitialCache
        ? initialCache?.videos || []
        : [];
    const [query, setQuery] = useState(initialQuery);
    const [videos, setVideos] = useState<Video[]>(initialCachedVideos);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(hasInitialCache);
    const [submittedQuery, setSubmittedQuery] = useState(initialQuery);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchErrorDetail, setSearchErrorDetail] = useState<string | null>(null);
    const {
        addDiscoveredVideos,
        cacheSearchResults,
        clearSearchCache,
        allowlist,
        denylist,
        discoveredVideos,
        watchHistory
    } = useAppStore();

    const performSearch = useCallback(async (q: string) => {
        const trimmedQuery = q.trim();
        if (!trimmedQuery) return;

        setSubmittedQuery(trimmedQuery);
        setSearched(true);
        setSearchError(null);
        setSearchErrorDetail(null);

        const queryKey = searchCacheKey(trimmedQuery);
        const cached = useAppStore.getState().searchCache;
        if (cached?.queryKey === queryKey) {
            setVideos(cached.videos);
            setLoading(false);
            return;
        }

        // This product intentionally keeps only the most recent query. A new
        // query invalidates the previous result set before any network call.
        clearSearchCache();
        setVideos([]);
        setLoading(true);
        try {
            const r = await fetch(`/api/search?q=${encodeURIComponent(trimmedQuery)}`, { credentials: 'include' });
            if (!r.ok) {
                const failure = await readSearchFailure(r);
                setSearchError(failure.message);
                setSearchErrorDetail(failure.detail);
                setVideos([]);
                return;
            }
            const data = await r.json();
            const nextVideos = (data.videos || []) as Video[];
            setVideos(nextVideos);
            addDiscoveredVideos(nextVideos);
            cacheSearchResults(queryKey, nextVideos);
        } catch (error) {
            const failure = readNetworkSearchFailure(error);
            setSearchError(failure.message);
            setSearchErrorDetail(failure.detail);
            setVideos([]);
        } finally {
            setLoading(false);
        }
    }, [addDiscoveredVideos, cacheSearchResults, clearSearchCache]);

    useEffect(() => {
        const q = searchParams.get('q');
        if (q) {
            setQuery(q);
            performSearch(q);
        }
    }, [searchParams, performSearch]);

    const localMatches = useMemo(() => {
        if (!submittedQuery.trim()) return [];
        return discoveredVideos.filter(video => videoMatchesSearchQuery(video, submittedQuery));
    }, [discoveredVideos, submittedQuery]);

    const combinedVideos = useMemo(() => {
        const videosById = new Map<string, Video>();

        for (const video of videos) {
            videosById.set(video.id, video);
        }

        for (const video of localMatches) {
            if (!videosById.has(video.id)) {
                videosById.set(video.id, video);
            }
        }

        return [...videosById.values()];
    }, [videos, localMatches]);

    const filteredVideos = useMemo(() => {
        const watchedVideoIds = getWatchedVideoIds(watchHistory);
        const allowedVideos = filterVideos(combinedVideos, {
            allowlist,
            denylist,
            requireMadeForKids: true
        });
        return orderVideosByFreshnessThenWatchStatus(allowedVideos, watchedVideoIds);
    }, [allowlist, combinedVideos, denylist, watchHistory]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim()) setSearchParams({ q: query.trim() });
    };

    return (
        <div className="search-page">
            <Link to="/" className="search-back">← Back</Link>

            <form onSubmit={handleSubmit} className="search-form">
                <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="What do you want to watch?"
                        autoFocus
                        className="search-input"
                    />
                </div>
                <button
                    type="submit"
                    disabled={!query.trim()}
                    className="search-submit"
                >
                    Search
                </button>
            </form>

            {loading && (
                <div className="search-loading">
                    <div className="search-loading__icon">🔍</div>
                    <p>Searching...</p>
                </div>
            )}

            {!loading && searched && searchError && filteredVideos.length > 0 && (
                <div className="search-error" role="status">
                    <p>{searchError}</p>
                    {searchErrorDetail && <p className="search-error__detail">{searchErrorDetail}</p>}
                </div>
            )}

            {!loading && searched && filteredVideos.length === 0 && (
                <div className="search-empty">
                    <span className="search-empty__icon">🤔</span>
                    <h2>{searchError ? 'Search is having trouble' : 'No videos found'}</h2>
                    <p>{searchError ? 'Please try again soon' : 'Try a different search'}</p>
                    {searchErrorDetail && <p className="search-empty__detail">{searchErrorDetail}</p>}
                </div>
            )}

            {!loading && filteredVideos.length > 0 && (
                <div className="search-results">
                    {filteredVideos.map(v => (
                        <VideoCard key={v.id} video={v} />
                    ))}
                </div>
            )}
        </div>
    );
}
